import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Check, ExternalLink, TrendingUp, Wallet, CircleDollarSign } from 'lucide-react';
import { 
  searchTokens, 
  getWalletTokensWithMetadata, 
  getBatchWalletTokensWithMetadata,
  refreshWalletTokens 
} from '../services/solanatrackerService';
import { Token } from '../types/supabase';
import { getUserProfile } from '../services/userProfileService';
import { useWalletUser } from '../hooks/useWalletUser';
import { ekkoColors, injectGlobalStyles } from '../styles/globalWidgetStyles';

interface TokenSearchResult {
  name: string;
  symbol: string;
  mint: string;
  decimals: number;
  image?: string;
  holders?: number;
  verified?: boolean;
  liquidityUsd: number | undefined; // Allow undefined to match API reality
  marketCapUsd: number | undefined; // Allow undefined to match API reality
  priceUsd: number | undefined; // Allow undefined to match API reality
  volume_24h?: number;
  market?: string;
}

interface WalletToken {
  mint: string;
  name: string;
  symbol: string;
  decimals: number;
  image: string | null;
  balance: string;
  uiBalance: string;
  value: number;
  price: {
    quote: number;
    usd: number;
  };
}

interface TokenSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectToken: (token: Token, balance?: string) => void;
  referenceElement?: HTMLElement | null; // Optional reference element to position against
}

// Update custom styles for the popout appearance using white theme
const popoutStyles = `
  .token-search-popout {
    position: fixed;
    z-index: 99999;
    background: white;
    backdrop-filter: blur(8px);
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(36, 195, 91, 0.2);
    overflow: hidden;
    animation: popoutAppear 0.2s ease;
  }
  
  @keyframes popoutAppear {
    0% {
      opacity: 0;
      transform: scale(0.97);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .search-header {
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .token-search-input {
    background: white;
    border: 1px solid #e5e7eb;
    color: #000000;
  }
  
  .token-search-input:focus {
    border-color: #24c35b;
    box-shadow: 0 0 0 1px rgba(36, 195, 91, 0.2);
    outline: none;
  }
  
  .search-button {
    background: #24c35b;
    color: #000000;
    border: none;
    font-weight: 500;
  }
  
  .search-button:hover:not(:disabled) {
    background: #20a855;
  }
  
  .search-button:disabled {
    background: rgba(36, 195, 91, 0.3);
    color: rgba(0, 0, 0, 0.5);
  }
  
  .results-container {
    background: white;
    scrollbar-width: thin;
    scrollbar-color: rgba(36, 195, 91, 0.3) transparent;
  }
  
  .results-container::-webkit-scrollbar {
    width: 4px;
  }
  
  .results-container::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .results-container::-webkit-scrollbar-thumb {
    background-color: rgba(36, 195, 91, 0.3);
    border-radius: 2px;
  }
  
  .results-container::-webkit-scrollbar-thumb:hover {
    background-color: rgba(36, 195, 91, 0.5);
  }
  
  .result-item:hover {
    background: rgba(36, 195, 91, 0.1);
  }
  
  .token-search-footer {
    background: white;
    border-top: 1px solid #e5e7eb;
    font-size: 10px;
  }

  .wallet-tokens-section {
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .wallet-token-item {
    transition: all 0.15s ease;
  }
  
  .wallet-token-item:hover {
    background: rgba(36, 195, 91, 0.1);
  }

  .token-placeholder {
    background: rgba(36, 195, 91, 0.1);
    color: #24c35b;
  }

  .loading-spinner {
    border-color: rgba(36, 195, 91, 0.2);
    border-top-color: #24c35b;
  }
`;

// Simple search cache to avoid repeated API calls
const searchCache = new Map<string, { results: TokenSearchResult[]; timestamp: number }>();
const SEARCH_CACHE_DURATION = 60000; // 1 minute cache

const TokenSearchModal: React.FC<TokenSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectToken,
  referenceElement
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TokenSearchResult[]>([]);
  const [walletTokens, setWalletTokens] = useState<WalletToken[]>([]);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isLoadingWalletTokens, setIsLoadingWalletTokens] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const walletUser = useWalletUser();
  const [activeTradingWallet, setActiveTradingWallet] = useState<string | null>(null);
  
  // Debouncing for search input
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Calculate position based on reference element or center if not provided
  const [position, setPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', right: 'auto' });
  
  // Update position when reference element changes
  useEffect(() => {
    if (isOpen) {
      // If no reference element, center the modal
      if (!referenceElement) {
        setPosition({
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          right: 'auto'
        });
        return;
      }
      
      const rect = referenceElement.getBoundingClientRect();
      const modalWidth = 320;
      const modalHeight = 420;
      
      // Try to position modal to the right of the reference element first
      // If not enough space, try to the left
      const spaceToRight = window.innerWidth - rect.right;
      const spaceToLeft = rect.left;
      
      let left;
      let right = 'auto';
      
      if (spaceToRight >= modalWidth + 10) {
        // Position to the right
        left = `${rect.right + 10}px`;
      } else if (spaceToLeft >= modalWidth + 10) {
        // Position to the left
        left = 'auto';
        right = `${window.innerWidth - rect.left + 10}px`;
      } else {
        // Center horizontally if no good position on sides
        left = '50%';
        right = 'auto';
      }
      
      // Vertical positioning - try to center relative to the reference element
      let top = rect.top + (rect.height / 2) - (modalHeight / 2);
      
      // Ensure the modal stays within the viewport
      if (top < 20) top = 20;
      if (top + modalHeight > window.innerHeight - 20) {
        top = window.innerHeight - modalHeight - 20;
      }
      
      // Apply the calculated position
      setPosition({
        top: typeof top === 'number' ? `${top}px` : top,
        left,
        right,
        transform: left === '50%' ? 'translateX(-50%)' : 'none'
      });
    }
  }, [isOpen, referenceElement]);
  
  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  
  // Reset state when modal opens to ensure fresh state for each session
  useEffect(() => {
    if (isOpen) {
      // Reset all search-related state
      setSearchQuery('');
      setSearchResults([]);
      setError(null);
      setIsLoadingSearch(false);
      
      // Clear any pending search timeouts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      
      console.log('🔄 TokenSearchModal opened - state reset for fresh session');
    }
  }, [isOpen]);
  
  // Fetch the active trading wallet when the modal opens
  useEffect(() => {
    const fetchActiveTradingWallet = async () => {
      if (isOpen && walletUser.walletAddress) {
        try {
          const profile = await getUserProfile(walletUser.walletAddress);
          if (profile?.active_trade_wallet) {
            setActiveTradingWallet(profile.active_trade_wallet);
            fetchWalletTokens(profile.active_trade_wallet);
          } else {
            setWalletError('No active trading wallet found');
          }
        } catch (err) {
          console.error('Error fetching active trading wallet:', err);
          setWalletError('Error fetching active trading wallet');
        }
      }
    };

    fetchActiveTradingWallet();
  }, [isOpen, walletUser.walletAddress]);

  // Fetch wallet tokens using the new batch function for maximum speed
  const fetchWalletTokens = async (walletAddress: string, immediate = false) => {
    if (!walletAddress) return;
    
    setIsLoadingWalletTokens(true);
    setWalletError(null);
    
    try {
      console.log(`🚀 BATCH OPTIMIZED: Fetching wallet tokens using batch strategy for maximum speed`);
      
      // Use the new batch function even for single wallet (it has caching and optimizations)
      const batchResult = await getBatchWalletTokensWithMetadata([walletAddress.trim()]);
      
      if (batchResult.status === 'success' && batchResult.data && batchResult.data[walletAddress.trim()]) {
        const walletData = batchResult.data[walletAddress.trim()];
        
        if (walletData.tokens && Array.isArray(walletData.tokens) && walletData.tokens.length > 0) {
          setWalletTokens(walletData.tokens as WalletToken[]);
          console.log(`✅ BATCH OPTIMIZED: Loaded ${walletData.tokens.length} wallet tokens using batch strategy (MUCH faster!)`);
          return;
        } else {
          console.warn(`No tokens found in batch result for wallet ${walletAddress}`);
          setWalletTokens([]);
          setWalletError('No tokens found in wallet');
          return;
        }
      }
      
      // If batch failed, fallback to individual request
      console.warn('Batch request failed, falling back to individual request...');
      const result = await getWalletTokensWithMetadata(walletAddress.trim());
      
      if (result.status !== 'success' || !result.data?.tokens || !Array.isArray(result.data.tokens)) {
        console.warn(`No token data found for wallet ${walletAddress}`);
        setWalletTokens([]);
        setWalletError('No tokens found in wallet');
        return;
      }

      // Use the processed tokens directly from the service
      setWalletTokens(result.data.tokens as WalletToken[]);
      console.log(`✅ FALLBACK: Loaded ${result.data.tokens.length} wallet tokens using individual request`);
      
    } catch (err) {
      console.error('Error fetching wallet tokens:', err);
      
      // Final fallback to refresh method
      try {
        console.log(`🔄 FINAL FALLBACK: Trying refresh method...`);
        const refreshResult = await refreshWalletTokens(walletAddress, undefined, immediate);
        
        if (refreshResult.status === 'success' && refreshResult.data?.tokens) {
          setWalletTokens(refreshResult.data.tokens as WalletToken[]);
          console.log(`⚠️ FINAL FALLBACK: Loaded tokens using refresh method`);
        } else {
          setWalletTokens([]);
          setWalletError('No tokens found in wallet');
        }
      } catch (fallbackErr) {
        console.error('All methods failed:', fallbackErr);
        setWalletError('Failed to load wallet tokens');
        setWalletTokens([]);
      }
    } finally {
      setIsLoadingWalletTokens(false);
    }
  };
  
  // Listen for token balance update events and refresh wallet tokens
  useEffect(() => {
    const handleTokenBalanceUpdate = (event: Event) => {
      if (!activeTradingWallet) return;
      
      // Type assertion for the custom event
      const customEvent = event as CustomEvent<{address: string, mint: string, balance: string}>;
      
      // Verify this update is for our active wallet
      if (customEvent.detail.address === activeTradingWallet) {
        console.log('Balance updated, refreshing wallet tokens');
        fetchWalletTokens(activeTradingWallet);
      }
    };

    const handleTradeCompleted = (event: Event) => {
      if (!activeTradingWallet) return;
      
      // Type assertion for the custom event
      const customEvent = event as CustomEvent<{
        type: 'buy' | 'sell',
        token: any,
        amount: string,
        walletAddress: string,
        timestamp: number
      }>;
      
      // Verify this update is for our active wallet
      if (customEvent.detail.walletAddress === activeTradingWallet) {
        console.log(`🔄 Trade ${customEvent.detail.type} completed for ${customEvent.detail.token?.symbol || customEvent.detail.token?.mint}, immediately refreshing wallet tokens`);
        
        // Force an immediate aggressive refresh with no caching
        setTimeout(() => {
          console.log('Performing immediate post-trade refresh');
          fetchWalletTokens(activeTradingWallet, true);
        }, 500); // Small delay to let blockchain update
        
        // Also schedule a follow-up refresh after a longer delay
        setTimeout(() => {
          console.log('Performing follow-up post-trade refresh');
          fetchWalletTokens(activeTradingWallet, true);
        }, 2000);
      }
    };

    // Listen for the custom events
    window.addEventListener('ekko:token_balance_updated', handleTokenBalanceUpdate);
    window.addEventListener('ekko:trade_completed', handleTradeCompleted);

    return () => {
      window.removeEventListener('ekko:token_balance_updated', handleTokenBalanceUpdate);
      window.removeEventListener('ekko:trade_completed', handleTradeCompleted);
    };
  }, [activeTradingWallet]);
  
  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      
      // Clean up search timeout when modal closes or component unmounts
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      
      // Reset loading state if modal is closing
      if (!isOpen) {
        setIsLoadingSearch(false);
      }
    };
  }, [isOpen, onClose]);
  
  // Handle search with optimized performance and caching
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setError(null);
      return;
    }
    
    // Prevent multiple simultaneous searches
    if (isLoadingSearch) {
      console.log('🚫 Search already in progress, skipping duplicate request');
      return;
    }
    
    // Check cache first
    const cacheKey = searchQuery.toLowerCase().trim();
    const cached = searchCache.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < SEARCH_CACHE_DURATION) {
      console.log(`📋 CACHE HIT: Using cached search results for "${searchQuery}"`);
      setSearchResults(cached.results);
      setError(null);
      return;
    }
    
    setIsLoadingSearch(true);
    setError(null);
    
    try {
      console.log(`🔍 FAST SEARCH: Searching tokens for "${searchQuery}" with optimized strategy`);
      
      // Use optimized search with smaller limit for faster response
      const result = await searchTokens({
        query: searchQuery,
        limit: 15, // Reduced from 20 for faster response
        sortBy: 'liquidityUsd',
        sortOrder: 'desc'
      });
      
      // Check if we have results in a type-safe way
      if (result.status === 'success' && Array.isArray(result.data) && result.data.length > 0) {
        setSearchResults(result.data);
        setError(null);
        
        // Cache the results
        searchCache.set(cacheKey, {
          results: result.data,
          timestamp: now
        });
        
        console.log(`✅ FAST SEARCH: Found ${result.data.length} tokens quickly (cached for future use)`);
      } else {
        // No results or error - set empty array and possibly an error message
        setSearchResults([]);
        if (result.status === 'error') {
          setError(result.error || 'Failed to search tokens. Please try again.');
          console.log(`❌ FAST SEARCH: Search failed with error: ${result.error}`);
        } else {
          setError(null);
          console.log(`📭 FAST SEARCH: No tokens found for "${searchQuery}"`);
        }
      }
    } catch (err) {
      console.error('Error searching tokens:', err);
      setError('Failed to search tokens. Please try again.');
      setSearchResults([]);
    } finally {
      // Always reset loading state, even if component unmounted
      setIsLoadingSearch(false);
    }
  };
  
  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
    
    // Auto-search after 300ms of no typing (only if query is long enough)
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        // Double-check that the search query hasn't changed and modal is still open
        if (isOpen && value.trim() === searchQuery.trim()) {
          handleSearch();
        }
      }, 300);
    } else {
      // Clear results if query is too short
      setSearchResults([]);
      setError(null);
      setIsLoadingSearch(false);
    }
  };
  
  // Handle key press (Enter to search)
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      
      // Clear any pending debounced search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      
      // Trigger immediate search
      handleSearch();
    }
  };
  
  // Handle wallet token selection
  const handleSelectWalletToken = (walletToken: WalletToken) => {
    // Convert wallet token to the Token type
    const token = {
      id: walletToken.mint,
      mint: walletToken.mint,
      name: walletToken.name,
      symbol: walletToken.symbol,
      created_at: new Date().toISOString(),
      image_uri: walletToken.image,
      image_url: walletToken.image, // Set both fields for compatibility
      price_usd: walletToken.price?.usd || 0,
      liquidity_usd: 0, // Not provided in wallet token data
      market_cap_usd: 0, // Not provided in wallet token data
      holders: null,
      market: null
    } as Token;
    
    // Pass both the token and its balance to the parent component
    onSelectToken(token, walletToken.balance);
    onClose();
  };
  
  // Handle token selection
  const handleSelectToken = (result: TokenSearchResult) => {
    // Convert the search result to a partial Token type
    // Use type assertion since we're only setting some required properties
    const token = {
      id: result.mint,
      mint: result.mint,
      name: result.name,
      symbol: result.symbol,
      created_at: new Date().toISOString(),
      image_uri: result.image || null,
      image_url: result.image || null, // Set both fields for compatibility
      price_usd: result.priceUsd || 0, // Default to 0 if undefined
      liquidity_usd: result.liquidityUsd || 0, // Default to 0 if undefined
      market_cap_usd: result.marketCapUsd || 0, // Default to 0 if undefined
      holders: result.holders || null,
      market: result.market || null
    } as Token;
    
    // For tokens from search, we don't have a balance, so we pass undefined
    onSelectToken(token);
    onClose();
  };
  
  // Format numbers for display
  const formatNumber = (num: number | undefined, prefix = '') => {
    if (num === undefined) return `${prefix}0.00`;
    
    if (num >= 1_000_000_000) {
      return `${prefix}${(num / 1_000_000_000).toFixed(2)}B`;
    } else if (num >= 1_000_000) {
      return `${prefix}${(num / 1_000_000).toFixed(2)}M`;
    } else if (num >= 1_000) {
      return `${prefix}${(num / 1_000).toFixed(2)}K`;
    } else {
      return `${prefix}${num.toFixed(2)}`;
    }
  };
  
  // Format price with appropriate decimal places
  const formatPrice = (price: number | undefined) => {
    // Handle undefined or null values
    if (price === undefined || price === null || isNaN(price)) {
      return '$0.00';
    }
    
    if (price < 0.00001) {
      return '$' + price.toExponential(2);
    } else if (price < 0.001) {
      return '$' + price.toFixed(6);
    } else if (price < 1) {
      return '$' + price.toFixed(4);
    } else if (price < 10) {
      return '$' + price.toFixed(2);
    } else {
      return '$' + price.toFixed(2);
    }
  };

  // Format token balance
  const formatBalance = (balance: string, decimals: number = 9) => {
    const num = parseFloat(balance);
    if (isNaN(num)) return '0';
    
    // For UI purposes, show different precision for different ranges
    if (num < 0.001) return num.toExponential(2);
    if (num < 1) return num.toFixed(4);
    if (num < 1000) return num.toFixed(2);
    return formatNumber(num);
  };
  
  if (!isOpen) return null;
  
  // Use createPortal to render the modal directly to the body
  // This ensures it's not constrained by any parent components
  return createPortal(
    <>
      {injectGlobalStyles()}
      <style>{popoutStyles}</style>
      <div
        className="fixed inset-0 bg-black/20 z-[99990]"
        onClick={onClose}
        style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
      />
      <div
        ref={modalRef}
        className="token-search-popout w-[480px] max-h-[420px] flex flex-col relative rounded-lg overflow-hidden backdrop-blur-sm border border-[#24c35b]/20 shadow-xl"
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          right: position.right,
          transform: position.transform,
          background: 'white',
          zIndex: 99999
        }}
      >
        {/* Background masking layer */}
        <div className="absolute inset-0 bg-white rounded-lg -z-10"></div>
        
        {/* Header */}
        <div className="search-header flex items-center justify-between p-2 relative z-10 rounded-t-lg">
          <h2 className="text-xs font-medium text-black flex items-center">
            <Search size={12} className="mr-1 text-[#24c35b]" />
            Search Tokens
          </h2>
          <button 
            onClick={onClose}
            className="p-0.5 rounded-sm hover:bg-[#24c35b]/10 transition-colors text-gray-600 hover:text-[#24c35b]"
          >
            <X size={14} />
          </button>
        </div>
        
        {/* Search input */}
        <div className="p-2 bg-white">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Type 2+ chars to search tokens..."
              className="token-search-input w-full pl-2 pr-16 py-1.5 text-xs rounded"
            />
            <button
              onClick={handleSearch}
              disabled={isLoadingSearch || !searchQuery.trim()}
              className="search-button absolute right-0 h-full px-2 text-xs font-medium rounded-r"
            >
              {isLoadingSearch ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
        
        {/* Search results */}
        <div className="results-container flex-1 overflow-y-auto relative z-10">
          {/* My Wallet Tokens Section */}
          <div className="wallet-tokens-section">
            <div className="flex justify-between items-center px-3 py-1.5 bg-white">
              <div className="text-[10px] font-medium text-black flex items-center">
                <Wallet size={10} className="mr-1 text-[#24c35b]" />
                My Wallet Tokens
              </div>
              {activeTradingWallet && (
                <div className="text-[9px] text-gray-600">
                  {activeTradingWallet.slice(0, 4)}...{activeTradingWallet.slice(-4)}
                </div>
              )}
            </div>

            {isLoadingWalletTokens ? (
              <div className="p-4 flex items-center justify-center">
                <div className="loading-spinner animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#24c35b]"></div>
              </div>
            ) : walletError ? (
              <div className="p-2 text-center text-gray-600 text-[10px]">
                {walletError}
              </div>
            ) : !walletTokens || walletTokens.length === 0 ? (
              <div className="p-2 text-center text-gray-600 text-[10px]">
                No tokens found in your wallet
              </div>
            ) : (
              <div className="divide-y divide-gray-700/20 max-h-[150px] overflow-y-auto">
                {Array.isArray(walletTokens) && walletTokens
                  .filter(walletToken => {
                    // Filter out tokens with zero balance
                    const balance = parseFloat(walletToken.balance);
                    return !isNaN(balance) && balance > 0;
                  })
                  .map((walletToken) => (
                  <button
                    key={walletToken.mint}
                    onClick={() => handleSelectWalletToken(walletToken)}
                    className="wallet-token-item grid grid-cols-12 gap-1 px-3 py-2 w-full text-left transition-colors"
                  >
                    <div className="col-span-7 flex items-center gap-1.5">
                      {walletToken.image ? (
                        <img 
                          src={walletToken.image}
                          alt={walletToken.symbol}
                          className="w-5 h-5 rounded-full bg-gray-700 object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/32/24c35b/FFFFFF/?text=' + (walletToken.symbol ? walletToken.symbol.substring(0, 1) : 'T');
                          }}
                        />
                      ) : (
                        <div className="token-placeholder w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[#24c35b] text-[10px] font-medium">
                          {walletToken.symbol ? walletToken.symbol.substring(0, 1) : 'T'}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-black text-xs flex items-center gap-0.5 truncate">
                          {walletToken.name.length > 14 ? walletToken.name.substring(0, 14) + '...' : walletToken.name}
                        </div>
                        <div className="text-[10px] text-gray-600">{walletToken.symbol}</div>
                      </div>
                    </div>
                    <div className="col-span-5 text-right flex flex-col justify-center">
                      <div className="font-medium text-[10px] text-[#24c35b]">
                        {formatBalance(walletToken.balance, walletToken.decimals)}
                      </div>
                      <div className="text-[10px] text-gray-600 flex flex-col">
                        <span>{formatPrice(walletToken.value || 0)} total</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search Results */}
          {error && (
            <div className="p-2 text-center text-red-500 text-xs">
              {error}
            </div>
          )}
          
          {isLoadingSearch ? (
            <div className="p-6 flex items-center justify-center">
              <div className="loading-spinner animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#24c35b]"></div>
            </div>
          ) : !searchResults || searchResults.length === 0 ? (
            searchQuery.trim() ? (
              <div className="p-4 text-center text-gray-600 text-xs">
                No tokens found. Try another search term.
              </div>
            ) : null
          ) : (
            <div className="divide-y divide-gray-200">
              <div className="grid grid-cols-12 gap-1 px-3 py-1.5 bg-gray-50 text-[10px] text-gray-700 font-medium">
                <div className="col-span-7">Token</div>
                <div className="col-span-5 text-right">Price</div>
              </div>
              
              {Array.isArray(searchResults) && searchResults.map((result) => (
                <button
                  key={result.mint}
                  onClick={() => handleSelectToken(result)}
                  className="result-item grid grid-cols-12 gap-1 px-3 py-2 w-full text-left transition-colors"
                >
                  <div className="col-span-7 flex items-center gap-1.5">
                    {result.image ? (
                      <img 
                        src={result.image}
                        alt={result.symbol}
                        className="w-5 h-5 rounded-full bg-gray-700 object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/32/24c35b/FFFFFF/?text=' + (result.symbol ? result.symbol.substring(0, 1) : 'T');
                        }}
                      />
                    ) : (
                      <div className="token-placeholder w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[#24c35b] text-[10px] font-medium">
                        {result.symbol ? result.symbol.substring(0, 1) : 'T'}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-black text-xs flex items-center gap-0.5 truncate">
                        {result.name.length > 14 ? result.name.substring(0, 14) + '...' : result.name}
                        {result.verified && (
                          <Check size={9} className="text-[#24c35b]" />
                        )}
                      </div>
                      <div className="text-[10px] text-gray-600">{result.symbol}</div>
                    </div>
                  </div>
                  
                  <div className="col-span-5 text-right flex flex-col justify-center">
                    <div className="font-medium text-[10px] text-black">{formatPrice(result.priceUsd)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="token-search-footer p-1.5 flex justify-between items-center text-[9px] text-gray-600 relative z-10 rounded-b-lg">
          <div>
            EKKO Search
          </div>
        </div>
        
        {/* Gradient border - top */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#24c35b]/20 to-transparent"></div>
        
        {/* Gradient border - bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#24c35b]/20 to-transparent"></div>
        
        {/* Gradient border - left */}
        <div className="absolute top-0 bottom-0 left-0 w-[1px] bg-gradient-to-b from-[#24c35b]/10 via-transparent to-[#24c35b]/10"></div>
        
        {/* Gradient border - right */}
        <div className="absolute top-0 bottom-0 right-0 w-[1px] bg-gradient-to-b from-[#24c35b]/10 via-transparent to-[#24c35b]/10"></div>
      </div>
    </>,
    document.body
  );
};

export default TokenSearchModal; 